function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { clsx } from 'clsx';
import * as React from 'react';
import { ColumnDefTemplate, flexRender, Header, HeaderContext, HeaderGroup } from '@tanstack/react-table';
import { Cell, Column, RowData, Table } from '@tanstack/table-core';
import { useVirtualizer } from '@tanstack/react-virtual';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { closestCenter, DndContext, DragMoveEvent, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import debounce from 'lodash/debounce';
import { arrayMove } from '@dnd-kit/helpers';
import { CheckboxReact } from 'common/components/checkbox';
import { ColumnDefMeta, Row } from 'common/components/table-react/types';
import { SvgIconReact } from 'common/components/svg-icon';
import { useTranslation } from 'common/i18n/hooks';
import { TooltipReact } from 'common/components/tooltip';
import { TooltipTextAlign } from 'common/components/tooltip/types';
import { getSelectionParams, getUpdatedColumnOrder, sortColumnsBasedOnOrder } from 'iron/common/components/table-react/utils';
import { Translatable } from 'common/i18n';
import styles from './index.module.styl';
enum DropPosition {
  BEFORE,
  AFTER,
}
export type TableReactProps<TData extends RowData> = {
  table: Table<TData>;
  isLoading?: boolean;
  selectionLimit?: number;
  disableHeaders?: boolean;
  customEmptyMessage?: Translatable;
  onRowClick?: (row: Row<TData>) => void;
  minTableWidth?: React.CSSProperties[`minWidth`]; // Set min width of table to control when it should overflow
  enableColumnReordering?: boolean;
  onColumnReorder?: (newOrder: string[], changedColumnId: string, oldPosition?: number, newPosition?: number) => void;
  enableColumnResizing?: boolean;
  onColumnResize?: (columnId: string, newSize: number) => void;
} & Pick<React.HtmlHTMLAttributes<HTMLDivElement>, `style`>; // Escape hatch for style override

const SELECTION_COLUMN_WIDTH_PX = 48;
type TableLayoutInfo = {
  totalColumnFractionSize: number;
  totalColumnFixedSize: number;
};
function calcTableLayoutInfo(columnHeaderGroups: Array<HeaderGroup<any>>, selectionColumnEnabled: boolean): TableLayoutInfo {
  let totalColumnFixedSize = 0;
  let totalColumnFractionSize = 0;
  columnHeaderGroups.forEach(headerGroup => {
    headerGroup.headers.forEach(header => {
      const columnMeta: ColumnDefMeta | undefined = header.column.columnDef.meta;
      if (columnMeta !== null && columnMeta !== void 0 && columnMeta.fixedSize) {
        totalColumnFixedSize += header.getSize(); // fixed size cols are initialized in useTable hook
      } else {
        totalColumnFractionSize += header.getSize();
      }
    });
  });
  if (selectionColumnEnabled) {
    totalColumnFixedSize += SELECTION_COLUMN_WIDTH_PX;
  }
  return {
    totalColumnFractionSize,
    totalColumnFixedSize
  };
}
function getColumnWidthStyle(column: Column<any, any>, layoutInfo: TableLayoutInfo): string {
  const columnMeta = column.columnDef.meta as ColumnDefMeta;
  if (columnMeta.fixedSize) {
    return "".concat(column.getSize(), "px"); // fixed size cols are initialized in useTable hook
  }
  return "calc((".concat(column.getSize(), " / ").concat(layoutInfo.totalColumnFractionSize, ") * (100% - ").concat(layoutInfo.totalColumnFixedSize, "px))");
}
function HeaderSortingCaret(props: {
  header: Header<unknown, unknown>;
}) {
  const columnSort: false | `asc` | `desc` = props.header.column.getIsSorted();
  // note: these classnames are defined in global stylesheet iron/common/stylesheets/components/mp-table.styl
  return <div className={clsx(styles.sortControls, {
    [styles["sort".concat(columnSort)]]: columnSort
  })} data-sentry-component="HeaderSortingCaret" data-sentry-source-file="index.tsx">
      <SvgIconReact className={clsx(styles.sortCaret, styles.sortCaretUp)} icon='caret-up' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
      <SvgIconReact className={clsx(styles.sortCaret, styles.sortCaretDown)} icon='caret-down' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
    </div>;
}
function DraggableTableHeader(props: {
  header: Header<unknown, unknown>;
  getHeaderTemplate: (prop: any) => any;
  layoutInfo: TableLayoutInfo;
  dropPosition: DropPosition | null;
  registerNode: (id: string, node: HTMLElement | null) => void;
  showResizer?: boolean;
}) {
  var _header$column$column, _header$column$column2;
  const {
    header,
    layoutInfo,
    getHeaderTemplate: getHeaderTemplateFn,
    registerNode,
    dropPosition,
    showResizer
  } = props;
  const {
    attributes,
    listeners,
    setActivatorNodeRef,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: header.column.id
  });
  const combinedRefCallback = React.useCallback((node: HTMLElement | null) => {
    registerNode(header.column.id, node);
    setNodeRef(node);
  }, [header.column.id, setNodeRef, registerNode]);
  const style: React.CSSProperties = {
    transition,
    transform: CSS.Translate.toString(transform) // translate instead of transform to avoid squishing
  };
  const headerString = (_header$column$column = header.column.columnDef.header) === null || _header$column$column === void 0 ? void 0 : _header$column$column.toString();
  const headerStyleOverrides = (_header$column$column2 = header.column.columnDef.meta as ColumnDefMeta) === null || _header$column$column2 === void 0 ? void 0 : _header$column$column2.headerStyleOverrides;
  const isHeaderJustifyFlexEnd = (headerStyleOverrides === null || headerStyleOverrides === void 0 ? void 0 : headerStyleOverrides.justifyContent) === "flex-end";
  return <div className={clsx(styles.cell, {
    [styles.insertBefore]: dropPosition === DropPosition.BEFORE,
    [styles.insertAfter]: dropPosition === DropPosition.AFTER
  })} style={_objectSpread({
    flexBasis: getColumnWidthStyle(header.column, layoutInfo)
  }, headerStyleOverrides)} data-sentry-component="DraggableTableHeader" data-sentry-source-file="index.tsx">
      {isHeaderJustifyFlexEnd && <div style={{
      flexGrow: 1
    }} />}
      <div className={clsx(styles.header, {
      [styles.dragging]: isDragging,
      [styles.canSort]: header.column.getCanSort()
    })} onClick={header.column.getToggleSortingHandler()} style={style} ref={combinedRefCallback} {...attributes}>
        <div className={styles.dragHandle} ref={setActivatorNodeRef} {...listeners}>
          <SvgIconReact icon='gripper' data-sentry-element="SvgIconReact" data-sentry-source-file="index.tsx" />
        </div>
        <div className={styles.title}>{flexRender(getHeaderTemplateFn(header), header.getContext())}</div>
        {!isDragging && headerString && <TooltipReact textAlign={TooltipTextAlign.Center} delayIn={500}>
            {headerString}
          </TooltipReact>}
        {!isDragging && <HeaderSortingCaret header={header} />}
      </div>
      {!isHeaderJustifyFlexEnd && <div style={{
      flexGrow: 1
    }} />}
      {showResizer && <div className={clsx(styles.resizer, {
      [styles.isResizing]: header.column.getIsResizing()
    })} onMouseDown={header.getResizeHandler()} onTouchStart={header.getResizeHandler()} />}
    </div>;
}
function BasicTableHeader(props: {
  header: Header<unknown, unknown>;
  getHeaderTemplate: (prop: any) => any;
  layoutInfo: TableLayoutInfo;
  showResizer?: boolean;
}) {
  var _props$header$column$, _props$header$column$2;
  const headerString = (_props$header$column$ = props.header.column.columnDef.header) === null || _props$header$column$ === void 0 ? void 0 : _props$header$column$.toString();
  const headerStyleOverrides = (_props$header$column$2 = props.header.column.columnDef.meta as ColumnDefMeta) === null || _props$header$column$2 === void 0 ? void 0 : _props$header$column$2.headerStyleOverrides;
  const isHeaderJustifyFlexEnd = (headerStyleOverrides === null || headerStyleOverrides === void 0 ? void 0 : headerStyleOverrides.justifyContent) === "flex-end";
  return <div key={props.header.id} className={styles.cell} style={_objectSpread({
    flexBasis: getColumnWidthStyle(props.header.column, props.layoutInfo)
  }, headerStyleOverrides)} data-sentry-component="BasicTableHeader" data-sentry-source-file="index.tsx">
      {isHeaderJustifyFlexEnd && <div style={{
      flexGrow: 1
    }} />}
      <div className={clsx(styles.header, {
      [styles.canSort]: props.header.column.getCanSort()
    })} onClick={props.header.column.getToggleSortingHandler()}>
        <div className={styles.title}>
          {flexRender(props.getHeaderTemplate(props.header), props.header.getContext())}
        </div>
        {headerString && <TooltipReact textAlign={TooltipTextAlign.Center} delayIn={500}>
            {headerString}
          </TooltipReact>}
        <HeaderSortingCaret header={props.header} data-sentry-element="HeaderSortingCaret" data-sentry-source-file="index.tsx" />
      </div>
      {!isHeaderJustifyFlexEnd && <div style={{
      flexGrow: 1
    }} />}
      {props.showResizer && <div className={clsx(styles.resizer, {
      [styles.isResizing]: props.header.column.getIsResizing()
    })} onMouseDown={props.header.getResizeHandler()} onTouchStart={props.header.getResizeHandler()} />}
    </div>;
}
function TableHeaderFooter(props: {
  layoutInfo: TableLayoutInfo;
  headerGroups: Array<HeaderGroup<any>>;
  getHeaderTemplate: (header: Header<unknown, unknown>) => ColumnDefTemplate<HeaderContext<unknown, unknown>> | null | undefined;
  table: Table<any>;
  selectionLimit?: number;
  isLoading?: boolean;
  disableHeaders?: boolean;
  enableColumnReordering?: boolean;
  onColumnReorder?: TableReactProps<unknown>[`onColumnReorder`];
  enableColumnResizing?: boolean;
  onColumnResize?: TableReactProps<unknown>[`onColumnResize`];
}) {
  const {
    t
  } = useTranslation();
  const hasHeaderContent = props.headerGroups.flatMap(group => group.headers).some(header => !!props.getHeaderTemplate(header));
  const rowSelectionEnabled = props.table.options.enableRowSelection;
  const columnVisibility = props.table.getState().columnVisibility;
  const columnSizingInfo = props.table.getState().columnSizingInfo;
  const visibleColumns = props.table.getAllColumns().filter(col => col.getIsVisible());
  const [dropTarget, setDropTarget] = React.useState<{
    id: string;
    position: DropPosition;
  } | null>(null);
  const draggableHeadersMapRef = React.useRef<Map<string, HTMLElement>>(new Map());
  const isSelectionParamsSizeSetRef = React.useRef(false);
  const registerDraggableHeaderNode = React.useCallback((id: string, node: HTMLElement | null) => {
    if (node) {
      draggableHeadersMapRef.current.set(id, node);
    } else {
      draggableHeadersMapRef.current.delete(id);
    }
  }, []);
  React.useEffect(() => {
    // ensure column reordering is enabled
    if (props.enableColumnReordering) {
      const selectedColumns = props.table.getAllColumns().filter(column => {
        var _getSelectionParams;
        return (_getSelectionParams = getSelectionParams(column)) === null || _getSelectionParams === void 0 ? void 0 : _getSelectionParams.isSelected;
      });
      const columnOrder = props.table.getState().columnOrder;
      if (selectedColumns.length > 0 && columnOrder.length === 0) {
        // set initial sorted order of columns based on columnSelectionParams if not set already
        const sortedOrder = sortColumnsBasedOnOrder(selectedColumns);
        props.table.setColumnOrder(sortedOrder);
      }
    }
  }, [props.table, props.enableColumnReordering, columnVisibility]);
  React.useEffect(() => {
    // ensure column reordering is enabled
    if (props.enableColumnReordering) {
      const oldColumnOrder = props.table.getState().columnOrder;
      if (visibleColumns.length > 0 && oldColumnOrder.length > 0 && visibleColumns.length !== oldColumnOrder.length) {
        var _props$onColumnReorde;
        // set the correct order when columns are added/removed
        const {
          newOrder,
          affectedColIds
        } = getUpdatedColumnOrder(visibleColumns, oldColumnOrder);
        props.table.setColumnOrder(newOrder);
        (_props$onColumnReorde = props.onColumnReorder) === null || _props$onColumnReorde === void 0 || _props$onColumnReorde.call(props, newOrder, affectedColIds[0]);
      }
    }
  }, [props, visibleColumns, columnVisibility]);
  React.useEffect(() => {
    if (props.enableColumnResizing && columnSizingInfo.isResizingColumn) {
      var _columnSizingInfo$sta, _columnSizingInfo$del, _props$onColumnResize;
      const columnId = columnSizingInfo.isResizingColumn;
      const newSize = ((_columnSizingInfo$sta = columnSizingInfo.startSize) !== null && _columnSizingInfo$sta !== void 0 ? _columnSizingInfo$sta : 0) + ((_columnSizingInfo$del = columnSizingInfo.deltaOffset) !== null && _columnSizingInfo$del !== void 0 ? _columnSizingInfo$del : 0);
      (_props$onColumnResize = props.onColumnResize) === null || _props$onColumnResize === void 0 || _props$onColumnResize.call(props, columnId, newSize);
    }
  }, [props, columnSizingInfo]);
  React.useEffect(() => {
    // ensure column resizing is enabled and set initial sizing
    if (props.enableColumnResizing && !isSelectionParamsSizeSetRef.current) {
      const sizedColumns = visibleColumns.filter(column => {
        var _getSelectionParams2;
        return (_getSelectionParams2 = getSelectionParams(column)) === null || _getSelectionParams2 === void 0 ? void 0 : _getSelectionParams2.size;
      });
      if (sizedColumns.length > 0) {
        const initialSizing = sizedColumns.reduce((acc, column) => {
          var _getSelectionParams3;
          const columnSize = (_getSelectionParams3 = getSelectionParams(column)) === null || _getSelectionParams3 === void 0 ? void 0 : _getSelectionParams3.size;
          if (columnSize) {
            acc[column.id] = columnSize;
            isSelectionParamsSizeSetRef.current = true;
          }
          return acc;
        }, {} as Record<string, number>);
        props.table.setColumnSizing(old => _objectSpread(_objectSpread({}, old), initialSizing));
      }
    }
  }, [props.table, props.enableColumnResizing, visibleColumns]);
  const handleToggleAllSelected = () => {
    if (props.selectionLimit) {
      const affectedRows = props.table.getRowModel().rows.slice(0, props.selectionLimit);
      if (props.table.getIsSomeRowsSelected()) {
        // toggle all to false if some/all are selected
        affectedRows.forEach(row => {
          row.toggleSelected(false);
        });
      } else {
        // toggle all to true if none are selected
        affectedRows.forEach(row => {
          row.toggleSelected(true);
        });
      }
    } else {
      props.table.toggleAllRowsSelected();
    }
  };
  const getIsChecked = () => {
    if (props.selectionLimit) {
      const affectedRows = props.table.getRowModel().rows.slice(0, props.selectionLimit);
      return affectedRows.every(row => row.getIsSelected());
    }
    return props.table.getIsAllRowsSelected();
  };
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 10
    }
  }), useSensor(KeyboardSensor));
  if (hasHeaderContent && !props.disableHeaders) {
    return <DndContext collisionDetection={closestCenter} onDragEnd={event => {
      const {
        active
      } = event;
      if (dropTarget && active.id !== dropTarget.id) {
        props.table.setColumnOrder(oldOrder => {
          var _props$onColumnReorde2;
          const oldIndex = oldOrder.indexOf(active.id as string);
          let newIndex = oldOrder.indexOf(dropTarget.id as string);
          if (dropTarget.position === DropPosition.AFTER) newIndex += 1;
          if (oldIndex < newIndex) newIndex -= 1;
          const newOrder = arrayMove(oldOrder, oldIndex, newIndex);
          (_props$onColumnReorde2 = props.onColumnReorder) === null || _props$onColumnReorde2 === void 0 || _props$onColumnReorde2.call(props, newOrder, active.id as string, oldIndex, newIndex);
          return newOrder;
        });
      }
      setDropTarget(null);
    }} onDragMove={(event: DragMoveEvent) => {
      const {
        active,
        over
      } = event;
      if (!active || !over) {
        return;
      }
      const overElement = draggableHeadersMapRef.current.get(over.id as string);
      const activeElement = draggableHeadersMapRef.current.get(active.id as string);
      if (!overElement || !activeElement) return;
      const overRect = overElement.getBoundingClientRect();
      const activeRect = activeElement.getBoundingClientRect();
      const mid = activeRect.left - (overRect.left + overRect.width / 2);
      const position = mid < 0 ? DropPosition.BEFORE : DropPosition.AFTER;
      setDropTarget({
        id: over.id as string,
        position
      });
    }} sensors={sensors} modifiers={[restrictToFirstScrollableAncestor]}>
        <div className={styles.tableHeader}>
          {!props.isLoading && rowSelectionEnabled && <div className={clsx(styles.cell, styles.selection)} style={{
          flexBasis: "".concat(SELECTION_COLUMN_WIDTH_PX, "px")
        }}>
              <CheckboxReact role='checkbox' className={styles.selectionBox} checked={getIsChecked()} onClick={handleToggleAllSelected} uncheckedIcon={props.table.getIsSomeRowsSelected() ? "dash" : undefined} />
              {props.selectionLimit && <TooltipReact text={t("components.table.selection-limit-notice", {
            limit: props.selectionLimit
          })} />}
            </div>}
          {props.headerGroups.map(headerGroup => <React.Fragment key={headerGroup.id}>
              <SortableContext items={headerGroup.headers.map(header => header.id)} strategy={verticalListSortingStrategy}>
                {headerGroup.headers.map(header => {
              var _getSelectionParams4;
              if (header.isPlaceholder) {
                return null;
              }
              if (props.enableColumnReordering && (_getSelectionParams4 = getSelectionParams(header.column)) !== null && _getSelectionParams4 !== void 0 && _getSelectionParams4.canBeHidden) {
                const dropPosition = (dropTarget === null || dropTarget === void 0 ? void 0 : dropTarget.id) === header.id ? dropTarget.position : null;
                return <DraggableTableHeader key={header.id} header={header} getHeaderTemplate={props.getHeaderTemplate} layoutInfo={props.layoutInfo} dropPosition={dropPosition} registerNode={registerDraggableHeaderNode} showResizer={props.enableColumnResizing} />;
              }
              return <BasicTableHeader key={header.id} header={header} getHeaderTemplate={props.getHeaderTemplate} layoutInfo={props.layoutInfo} showResizer={props.enableColumnResizing} />;
            })}
              </SortableContext>
            </React.Fragment>)}
        </div>
      </DndContext>;
  }
  return null;
}
function getHeaderTemplate(header: Header<unknown, unknown>): ColumnDefTemplate<HeaderContext<unknown, unknown>> | null | undefined {
  return header.column.columnDef.header;
}
function getFooterTemplate(header: Header<unknown, unknown>): ColumnDefTemplate<HeaderContext<unknown, unknown>> | null | undefined {
  return header.column.columnDef.footer;
}
function TableCell<TData extends RowData>(props: {
  cell: Cell<TData, unknown>;
  layoutInfo: TableLayoutInfo;
}) {
  const {
    cell,
    layoutInfo
  } = props;
  return <div key={cell.id} className={styles.cell} style={{
    flexBasis: getColumnWidthStyle(cell.column, layoutInfo)
  }} data-sentry-component="TableCell" data-sentry-source-file="index.tsx">
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </div>;
}
const MemoizedTableCell = React.memo(TableCell, (prevProps, nextProps) => {
  return prevProps.cell.getValue() === nextProps.cell.getValue();
}) as typeof TableCell;
export function TableReact<TData extends RowData>(props: TableReactProps<TData>) {
  const {
    t,
    print
  } = useTranslation();
  const tableContainerRef = React.useRef<HTMLDivElement>(null);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [isScrolledToRight, setIsScrolledToRight] = React.useState(false);
  const {
    table,
    isLoading,
    onRowClick
  } = props;
  const rowSelectionEnabled = !!props.table.options.enableRowSelection;
  const headerGroups = table.getHeaderGroups();
  const rows = table.getRowModel().rows;
  const columnSizingInfo = table.getState().columnSizingInfo;
  const layoutInfo = React.useMemo(() => {
    if (columnSizingInfo.isResizingColumn) {
      return calcTableLayoutInfo(headerGroups, rowSelectionEnabled);
    }
    return calcTableLayoutInfo(headerGroups, rowSelectionEnabled);
  }, [headerGroups, rowSelectionEnabled, columnSizingInfo]);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    estimateSize: React.useCallback(() => 85, []),
    //estimate row height for accurate scrollbar dragging
    getScrollElement: () => tableContainerRef.current,
    overscan: 5
  });
  const checkIfScrolledToRight = React.useCallback(() => debounce(() => {
    const scrollContainer = scrollContainerRef === null || scrollContainerRef === void 0 ? void 0 : scrollContainerRef.current;
    if (scrollContainer && scrollContainer.parentElement) {
      const scrolledToRight = Math.round(scrollContainer.scrollLeft + scrollContainer.parentElement.clientWidth) >= scrollContainer.scrollWidth;
      setIsScrolledToRight(scrolledToRight);
    }
  }, 20), [scrollContainerRef]);
  React.useEffect(() => {
    // on window resize, scroll bar position may change so recheck
    window.addEventListener("resize", checkIfScrolledToRight());
    return () => {
      window.removeEventListener("resize", checkIfScrolledToRight());
    };
  }, []);
  const virtualItems = rowVirtualizer.getVirtualItems();
  // Instead of using absolute positioning (the default strategy for virtualizer), using a padding-based scroll manipulation
  // Absolute positioning breaks our z-index overlays like tooltips and context menus.
  const [paddingTop, paddingBottom] = virtualItems.length > 0 ? [Math.max(0, virtualItems[0].start - rowVirtualizer.options.scrollMargin), Math.max(0, rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end)] : [0, 0];
  const scrollContainerStyle = React.useMemo<React.CSSProperties>(() => _objectSpread({
    display: "inline-block",
    minWidth: props.minTableWidth
  }, props.minTableWidth === "100%" ? {
    width: layoutInfo.totalColumnFixedSize + 20
  } : {
    width: "100%"
  }), [layoutInfo.totalColumnFixedSize, props.minTableWidth]);
  return <div className={clsx(styles.root, {
    [styles.scrollRightShadow]: !isScrolledToRight
  })} onScroll={checkIfScrolledToRight()} ref={scrollContainerRef} data-sentry-component="TableReact" data-sentry-source-file="index.tsx">
      <div style={scrollContainerStyle}>
        <TableHeaderFooter layoutInfo={layoutInfo} headerGroups={headerGroups} getHeaderTemplate={getHeaderTemplate} table={table} selectionLimit={props.selectionLimit} isLoading={isLoading} disableHeaders={props.disableHeaders} enableColumnReordering={props.enableColumnReordering} onColumnReorder={props.onColumnReorder} enableColumnResizing={props.enableColumnResizing} onColumnResize={props.onColumnResize} data-sentry-element="TableHeaderFooter" data-sentry-source-file="index.tsx" />
        <div className={clsx(styles.table, {
        [styles.isLoading]: isLoading
      })} ref={tableContainerRef} style={props.style}>
          {isLoading ? <div className={clsx(styles.tableRow, styles.loading)} elref='mpTableRowsLoading'>
              {[...Array(5)].map((_, index) => <div key={index} className={clsx(styles.mpSkeletonLarge, styles.loadingSkeleton)} />)}
            </div> : rows.length > 0 ? <div className={styles.tableBody} style={{
          paddingTop,
          paddingBottom
        }}>
              {rowVirtualizer.getVirtualItems().map(virtualItem => {
            const row = rows[virtualItem.index];
            const cells = row.getVisibleCells().map(cell => {
              if (columnSizingInfo.isResizingColumn && columnSizingInfo.isResizingColumn !== cell.column.id) {
                return <MemoizedTableCell key={cell.id} cell={cell} layoutInfo={layoutInfo} />;
              }
              return <TableCell key={cell.id} cell={cell} layoutInfo={layoutInfo} />;
            });
            return <div role='row' key={row.id} data-index={virtualItem.index} ref={node => rowVirtualizer.measureElement(node)} className={clsx(styles.tableRow, {
              [styles.selected]: row.getIsSelected(),
              [styles.clickable]: !!onRowClick
            })} style={{
              display: "flex",
              width: "100%"
            }} onClick={onRowClick ? () => onRowClick(row) : undefined}>
                    {rowSelectionEnabled && <div className={clsx(styles.cell, styles.selection)} style={{
                flexBasis: "".concat(SELECTION_COLUMN_WIDTH_PX, "px")
              }}>
                        <CheckboxReact role='checkbox' className={styles.selectionBox} checked={row.getIsSelected()} onClick={row.getToggleSelectedHandler()} />
                      </div>}

                    {cells}
                  </div>;
          })}
            </div> : <div className={clsx(styles.tableRow, styles.empty)}>
              <div>
                {table.getState().globalFilter ? t("components.table.no-results") : props.customEmptyMessage ? print(props.customEmptyMessage) : t("components.table.empty")}
              </div>
            </div>}
        </div>
        <TableHeaderFooter layoutInfo={layoutInfo} headerGroups={table.getFooterGroups()} getHeaderTemplate={getFooterTemplate} table={table} data-sentry-element="TableHeaderFooter" data-sentry-source-file="index.tsx" />
      </div>
    </div>;
}